import "./CreateContentCalendarItemDialog.css";

import {
  AlertTriangle,
  CalendarPlus,
  X,
} from "lucide-react";

import {
  useEffect,
  useState,
} from "react";

import {
  CONTENT_CALENDAR_PRIORITIES,
  createContentCalendarItem,
  type ContentCalendarItem,
  type ContentCalendarPriority,
} from "../lib/contentCalendar";

interface CreateContentCalendarItemDialogProps {
  open:
    boolean;

  onClose:
    () => void;

  onCreated:
    (
      calendarItem:
        ContentCalendarItem,
    ) => void;
}

const priorityLabels:
  Record<
    ContentCalendarPriority,
    string
  > = {
    low:
      "Low",

    normal:
      "Normal",

    high:
      "High",

    urgent:
      "Urgent",
  };

function defaultTargetValue():
  string {
  const date =
    new Date();

  date.setDate(
    date.getDate() +
      7,
  );

  date.setMinutes(
    0,
    0,
    0,
  );

  const localDate =
    new Date(
      date.getTime() -
        date.getTimezoneOffset() *
          60_000,
    );

  return localDate
    .toISOString()
    .slice(
      0,
      16,
    );
}

function CreateContentCalendarItemDialog({
  open,
  onClose,
  onCreated,
}: CreateContentCalendarItemDialogProps) {
  const [
    topic,
    setTopic,
  ] =
    useState(
      "",
    );

  const [
    workingTitle,
    setWorkingTitle,
  ] =
    useState(
      "",
    );

  const [
    contentCategory,
    setContentCategory,
  ] =
    useState(
      "",
    );

  const [
    priority,
    setPriority,
  ] =
    useState<ContentCalendarPriority>(
      "normal",
    );

  const [
    targetPublishAt,
    setTargetPublishAt,
  ] =
    useState(
      defaultTargetValue,
    );

  const [
    assignedAuthor,
    setAssignedAuthor,
  ] =
    useState(
      "",
    );

  const [
    researchNotes,
    setResearchNotes,
  ] =
    useState(
      "",
    );

  const [
    preferredCta,
    setPreferredCta,
  ] =
    useState(
      "",
    );

  const [
    featuredImageRequirements,
    setFeaturedImageRequirements,
  ] =
    useState(
      "",
    );

  const [
    notes,
    setNotes,
  ] =
    useState(
      "",
    );

  const [
    isSubmitting,
    setIsSubmitting,
  ] =
    useState(
      false,
    );

  const [
    error,
    setError,
  ] =
    useState<
      string |
      null
    >(
      null,
    );

  useEffect(
    () => {
      if (
        !open
      ) {
        return;
      }

      setError(
        null,
      );

      const handleKeyDown =
        (
          event:
            KeyboardEvent,
        ) => {
          if (
            event.key ===
              "Escape" &&
            !isSubmitting
          ) {
            onClose();
          }
        };

      window.addEventListener(
        "keydown",
        handleKeyDown,
      );

      return () => {
        window.removeEventListener(
          "keydown",
          handleKeyDown,
        );
      };
    },
    [
      isSubmitting,
      onClose,
      open,
    ],
  );

  if (
    !open
  ) {
    return null;
  }

  return (
    <div
      className="calendar-dialog-backdrop"
      onMouseDown={(
        event,
      ) => {
        if (
          event.target ===
            event.currentTarget &&
          !isSubmitting
        ) {
          onClose();
        }
      }}
    >
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="create-calendar-title"
        className="calendar-dialog"
      >
        <header className="calendar-dialog-header">
          <div>
            <span className="section-eyebrow">
              Editorial planning
            </span>

            <h2 id="create-calendar-title">
              New calendar item
            </h2>

            <p>
              Add a controlled topic to the VEX Telecom production
              calendar.
            </p>
          </div>

          <button
            type="button"
            className="calendar-dialog-close"
            aria-label="Close creation form"
            disabled={isSubmitting}
            onClick={onClose}
          >
            <X
              aria-hidden
              size={19}
              strokeWidth={1.8}
            />
          </button>
        </header>

        <form
          className="calendar-create-form"
          onSubmit={async (
            event,
          ) => {
            event.preventDefault();

            setError(
              null,
            );

            if (
              !topic.trim() ||
              !workingTitle.trim() ||
              !contentCategory.trim() ||
              !targetPublishAt
            ) {
              setError(
                "Topic, working title, content category and publication target are required.",
              );

              return;
            }

            const targetDate =
              new Date(
                targetPublishAt,
              );

            if (
              Number.isNaN(
                targetDate.getTime(),
              )
            ) {
              setError(
                "Target publication must be a valid date and time.",
              );

              return;
            }

            setIsSubmitting(
              true,
            );

            try {
              const response =
                await createContentCalendarItem({
                  topic,

                  workingTitle,

                  contentCategory,

                  priority,

                  targetPublishAt:
                    targetDate.toISOString(),

                  assignedAuthor,

                  researchNotes,

                  preferredCta,

                  featuredImageRequirements,

                  notes,
                });

              onCreated(
                response.calendarItem,
              );
            } catch (
              submissionError
            ) {
              setError(
                submissionError instanceof
                  Error
                  ? submissionError.message
                  : "The calendar item could not be created.",
              );
            } finally {
              setIsSubmitting(
                false,
              );
            }
          }}
        >
          {error ? (
            <div
              className="calendar-form-error"
              role="alert"
            >
              <AlertTriangle
                aria-hidden
                size={17}
                strokeWidth={1.9}
              />

              <span>
                {error}
              </span>
            </div>
          ) : null}

          <div className="calendar-form-grid">
            <label className="calendar-form-field calendar-form-field-wide">
              <span>
                Topic
              </span>

              <input
                autoFocus
                required
                maxLength={300}
                value={topic}
                placeholder="Example: SIP failover"
                onChange={(
                  event,
                ) => {
                  setTopic(
                    event.target.value,
                  );
                }}
              />

              <small>
                {topic.length}/300
              </small>
            </label>

            <label className="calendar-form-field calendar-form-field-wide">
              <span>
                Working title
              </span>

              <input
                required
                maxLength={300}
                value={workingTitle}
                placeholder="How SIP Failover Protects Customer Calls"
                onChange={(
                  event,
                ) => {
                  setWorkingTitle(
                    event.target.value,
                  );
                }}
              />

              <small>
                {workingTitle.length}/300
              </small>
            </label>

            <label className="calendar-form-field">
              <span>
                Content category
              </span>

              <input
                required
                maxLength={120}
                value={contentCategory}
                placeholder="Business continuity"
                onChange={(
                  event,
                ) => {
                  setContentCategory(
                    event.target.value,
                  );
                }}
              />
            </label>

            <label className="calendar-form-field">
              <span>
                Priority
              </span>

              <select
                value={priority}
                onChange={(
                  event,
                ) => {
                  setPriority(
                    event.target.value as
                      ContentCalendarPriority,
                  );
                }}
              >
                {CONTENT_CALENDAR_PRIORITIES.map(
                  (
                    priorityOption,
                  ) => (
                    <option
                      key={priorityOption}
                      value={priorityOption}
                    >
                      {priorityLabels[
                        priorityOption
                      ]}
                    </option>
                  ),
                )}
              </select>
            </label>

            <label className="calendar-form-field">
              <span>
                Target publication
              </span>

              <input
                required
                type="datetime-local"
                value={targetPublishAt}
                onChange={(
                  event,
                ) => {
                  setTargetPublishAt(
                    event.target.value,
                  );
                }}
              />
            </label>

            <label className="calendar-form-field">
              <span>
                Assigned author
              </span>

              <input
                maxLength={200}
                value={assignedAuthor}
                placeholder="Optional"
                onChange={(
                  event,
                ) => {
                  setAssignedAuthor(
                    event.target.value,
                  );
                }}
              />
            </label>

            <label className="calendar-form-field calendar-form-field-wide">
              <span>
                Research notes
              </span>

              <textarea
                maxLength={10_000}
                rows={4}
                value={researchNotes}
                placeholder="Research direction, evidence requirements and important context."
                onChange={(
                  event,
                ) => {
                  setResearchNotes(
                    event.target.value,
                  );
                }}
              />
            </label>

            <label className="calendar-form-field calendar-form-field-wide">
              <span>
                Preferred call to action
              </span>

              <textarea
                maxLength={1_000}
                rows={2}
                value={preferredCta}
                placeholder="The intended next step for the reader."
                onChange={(
                  event,
                ) => {
                  setPreferredCta(
                    event.target.value,
                  );
                }}
              />
            </label>

            <label className="calendar-form-field calendar-form-field-wide">
              <span>
                Featured-image requirements
              </span>

              <textarea
                maxLength={3_000}
                rows={3}
                value={featuredImageRequirements}
                placeholder="Describe the required visual and official-logo placement."
                onChange={(
                  event,
                ) => {
                  setFeaturedImageRequirements(
                    event.target.value,
                  );
                }}
              />
            </label>

            <label className="calendar-form-field calendar-form-field-wide">
              <span>
                Internal notes
              </span>

              <textarea
                maxLength={3_000}
                rows={3}
                value={notes}
                placeholder="Optional audit notes for this calendar decision."
                onChange={(
                  event,
                ) => {
                  setNotes(
                    event.target.value,
                  );
                }}
              />
            </label>
          </div>

          <footer className="calendar-dialog-footer">
            <span>
              Submission creates a production calendar record and audit
              event.
            </span>

            <div>
              <button
                type="button"
                className="secondary-button"
                disabled={isSubmitting}
                onClick={onClose}
              >
                Cancel
              </button>

              <button
                type="submit"
                className="primary-button"
                disabled={isSubmitting}
              >
                <CalendarPlus
                  aria-hidden
                  size={16}
                  strokeWidth={1.9}
                />

                {isSubmitting
                  ? "Creating..."
                  : "Create calendar item"}
              </button>
            </div>
          </footer>
        </form>
      </section>
    </div>
  );
}

export default CreateContentCalendarItemDialog;
